import { useRef } from "react";
import * as THREE from "three";
import { useFrame } from "@react-three/fiber";
import { Html } from "@react-three/drei";
import { MapPin } from "lucide-react";
import { VisibleCanvas } from "./VisibleCanvas";
import { use3DEnabled } from "../../hooks/use3D";
import { useSiteContent } from "../../lib/api";

function MapScene() {
  const group = useRef();
  useFrame((state, dt) => {
    const g = group.current;
    if (!g) return;
    g.rotation.z += dt * 0.06;
    g.rotation.x = THREE.MathUtils.lerp(g.rotation.x, 1.02 + state.pointer.y * 0.08, 0.05);
  });
  return (
    <group ref={group} rotation={[1.02, 0, 0]} position={[0, -0.2, 0]}>
      {/* land disc */}
      <mesh rotation={[-Math.PI / 2, 0, 0]}>
        <cylinderGeometry args={[3.4, 3.4, 0.2, 64]} />
        <meshStandardMaterial color="#1c3a2b" metalness={0.1} roughness={0.85} />
      </mesh>
      <gridHelper args={[6.4, 16, "#2f5a43", "#274c39"]} position={[0, 0.12, 0]} rotation={[0, 0, 0]} />
      {/* 20km lime radius ring */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.16, 0]}>
        <ringGeometry args={[2.75, 2.95, 80]} />
        <meshBasicMaterial color="#62B848" transparent opacity={0.9} side={THREE.DoubleSide} />
      </mesh>
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.14, 0]}>
        <circleGeometry args={[2.85, 64]} />
        <meshBasicMaterial color="#62B848" transparent opacity={0.08} side={THREE.DoubleSide} />
      </mesh>
      {/* centre marker — South Perth */}
      <mesh position={[0, 0.5, 0]}>
        <coneGeometry args={[0.22, 0.55, 4]} />
        <meshStandardMaterial color="#62B848" emissive="#62B848" emissiveIntensity={0.6} />
      </mesh>
      <mesh position={[0, 0.9, 0]}>
        <sphereGeometry args={[0.13, 16, 16]} />
        <meshStandardMaterial color="#ffffff" />
      </mesh>
      <Html position={[0, 1.35, 0]} center distanceFactor={9} className="pointer-events-none">
        <div className="whitespace-nowrap rounded-full bg-[#62B848] text-[#0c1a12] font-bold text-xs px-3 py-1 shadow-lg">South Perth · base</div>
      </Html>
      <Html position={[2.9, 0.2, 0]} center distanceFactor={9} className="pointer-events-none">
        <div className="whitespace-nowrap rounded-full bg-white/10 backdrop-blur border border-[#62B848]/60 text-white font-bold text-[11px] px-2.5 py-1">20 km radius</div>
      </Html>
    </group>
  );
}

export function ServiceMapSection() {
  const enabled = use3DEnabled();
  const content = useSiteContent();

  return (
    <section data-testid="service-map-section" className="py-24 md:py-32 bg-[#0f1814] text-white overflow-hidden">
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 grid lg:grid-cols-2 gap-12 items-center">
        <div className="order-2 lg:order-1">
          <p className="text-[#62B848] font-bold tracking-wide uppercase text-sm mb-3">Service area</p>
          <h2 className="font-display font-extrabold heading-up uppercase text-3xl sm:text-4xl lg:text-5xl leading-none mb-4">
            {content.service_area_text}
          </h2>
          <p className="text-white/60 text-lg leading-relaxed mb-7 max-w-md">
            Based at Coode Street, South Perth, we cover the whole metro within a {content.service_area_radius_km} km radius — residential and commercial. Just outside? Send it through and we'll confirm.
          </p>
          <div className="flex items-center gap-3 text-white/80">
            <MapPin size={20} className="text-[#62B848]" />
            <span className="font-semibold">Coode Street, South Perth WA 6151</span>
          </div>
        </div>

        <div className="order-1 lg:order-2 relative h-[380px] md:h-[460px]">
          {enabled ? (
            <VisibleCanvas className="w-full h-full" camera={{ position: [0, 0, 7], fov: 42 }} dpr={[1, 1.8]}>
              <ambientLight intensity={0.7} />
              <directionalLight position={[4, 8, 5]} intensity={1.2} />
              <directionalLight position={[-5, 3, -4]} intensity={0.4} color="#62B848" />
              <MapScene />
            </VisibleCanvas>
          ) : (
            <div className="w-full h-full rounded-3xl border border-[#62B848]/30 grid place-items-center bg-[#62B848]/5">
              <div className="text-center">
                <MapPin size={40} className="text-[#62B848] mx-auto mb-3" />
                <div className="font-display font-bold text-xl">{content.service_area_radius_km} km around South Perth</div>
                <div className="text-white/60 text-sm mt-1">All of Perth metro</div>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
